import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

// Extend dayjs to handle custom formats
dayjs.extend(customParseFormat);

// Helper function to parse any date format
const parseDate = (date: string | Date): Date | null => {
  if (!date) return null;

  // If it's already a Date object
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }

  // Handle European format (DD/MM/YYYY)
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
    const [day, month, year] = date.split("/").map(Number);
    return new Date(year, month - 1, day);
  }

  // Handle ISO-like format with time (YYYY-MM-DD HH:mm:ss)
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(date)) {
    return new Date(date.replace(" ", "T"));
  }

  // Handle ISO format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return new Date(date);
  }

  // Try default Date parsing as fallback
  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? null : parsed;
};

// DayJS based formatter
export const formatDate = (date?: string | null) => {
  if (!date) return "N/A";

  const parsed = parseDate(date);
  if (!parsed) return "Invalid Date";

  return dayjs(parsed).format("MMM D, YYYY, h:mm A");
};

// Individual utility functions
export const formatDateToISO = (date: string | Date): string => {
  const parsed = parseDate(date);
  if (!parsed) return "N/A";

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** List tables: API may send ISO string, millis number, or Date. */
export const formatDateCreatedValue = (value: unknown): string => {
  if (value == null || value === "") return "N/A";
  if (typeof value === "number") return formatDateToISO(new Date(value));
  return formatDateToISO(value as string | Date);
};

export const formatDateToEuropean = (date: string | Date): string => {
  const parsed = parseDate(date);
  if (!parsed) return "N/A";

  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const year = parsed.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatDateToUS = (date: string | Date): string => {
  const parsed = parseDate(date);
  if (!parsed) return "N/A";

  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  const year = parsed.getFullYear();
  return `${month}/${day}/${year}`;
};

export const formatDateCustom = (
  date: string | Date,
  separator: string = "-"
): string => {
  const parsed = parseDate(date);
  if (!parsed) return "N/A";

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}${separator}${month}${separator}${day}`;
};

export const getRelativeTime = (date: string | Date): string => {
  const parsed = parseDate(date);
  if (!parsed) return "N/A";

  const now = new Date();
  const diffInMs = parsed.getTime() - now.getTime();
  const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "Tomorrow";
  if (diffInDays === -1) return "Yesterday";
  if (diffInDays > 1) return `In ${diffInDays} days`;
  if (diffInDays < -1) return `${Math.abs(diffInDays)} days ago`;

  return formatDateToEuropean(parsed);
};

export const isValidDate = (date: string | Date): boolean => {
  return parseDate(date) !== null;
};

// Object-based exports for convenience
export const dateFormatters = {
  toISO: formatDateToISO,
  toEuropean: formatDateToEuropean,
  toUS: formatDateToUS,
  toCustom: formatDateCustom,
  relative: getRelativeTime,
  isValid: isValidDate,
};

// Class-based approach (keeping your original class)
export class DateFormatter {
  static formatISO = formatDateToISO;
  static formatEuropean = formatDateToEuropean;
  static formatUS = formatDateToUS;
  static formatCustom = formatDateCustom;
  static getRelativeTime = getRelativeTime;
  static isValid = isValidDate;
}

// Delivery days
export const msToDaysHours = (ms: number) => {
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  return { days, hours };
};
